/**
 * Leviathan Web - Header Component
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Main navigation header with time display, quick actions, and user menu
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Bell,
  User,
  LogOut,
  Shield,
  Settings,
  Plus,
  Truck,
  FileText,
  Package,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Dashboard header component with tactical industrial styling
 */
const Header = () => {
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const menuRef = useRef<HTMLDivElement>(null);
  const quickMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (quickMenuRef.current && !quickMenuRef.current.contains(event.target as Node)) {
        setShowQuickMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'Operator';
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    if (user?.firstName) {
      return user.firstName[0];
    }
    return 'U';
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const quickActions = [
    { label: 'New Order', path: '/orders/new', icon: <FileText className="w-4 h-4" /> },
    { label: 'New Dispatch', path: '/dispatch/new', icon: <Truck className="w-4 h-4" /> },
    { label: 'New Invoice', path: '/invoices/new', icon: <FileText className="w-4 h-4" /> },
    { label: 'New Receipt', path: '/receipts/new', icon: <Package className="w-4 h-4" /> },
  ];

  return (
    <header className="h-16 bg-panel-dark border-b border-panel-border flex items-center px-6 shadow-panel relative z-50">
      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber/30" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber/30" />

      {/* Logo */}
      <div className="flex items-center space-x-3">
        <div
          className="w-8 h-8 bg-amber flex items-center justify-center"
          style={{
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          }}
        >
          <span className="text-background font-bold text-sm">L</span>
        </div>
        <h1
          className="text-xl font-display font-bold tracking-wider text-steel hidden md:block"
          style={{ textShadow: '0 0 10px rgba(92, 140, 199, 0.6)' }}
        >
          LEVIATHAN
        </h1>
      </div>

      {/* Center: Time display */}
      <div className="flex-1 flex justify-center">
        <div className="text-center hidden sm:block">
          <div
            className="text-amber font-mono text-lg tracking-wider"
            style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.5)' }}
          >
            {formatTime(currentTime)}
          </div>
          <div className="text-text-tertiary font-mono text-[10px] uppercase tracking-widest">
            {formatDate(currentTime)}
          </div>
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center space-x-2">
        {/* Quick actions menu */}
        <div className="relative" ref={quickMenuRef}>
          <button
            onClick={() => setShowQuickMenu(!showQuickMenu)}
            className="flex items-center space-x-1 px-3 py-2 bg-amber/10 hover:bg-amber/20 border border-amber/30 hover:border-amber text-amber transition-colors"
            title="Quick Actions"
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs font-mono uppercase tracking-wider hidden md:inline">New</span>
          </button>

          {showQuickMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-panel-dark border border-amber/50 shadow-lg z-50">
              <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
              <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

              <div className="py-1">
                {quickActions.map((action) => (
                  <Link
                    key={action.path}
                    to={action.path}
                    onClick={() => setShowQuickMenu(false)}
                    className="flex items-center space-x-3 px-4 py-2 text-text-secondary hover:text-amber hover:bg-amber/5 font-mono text-xs uppercase tracking-wider transition-colors"
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Dispatch shortcut */}
        <button
          onClick={() => navigate('/dispatch')}
          className="p-2 text-text-tertiary hover:text-amber hover:bg-panel-border/50 transition-colors"
          title="Dispatch Board"
        >
          <Truck className="w-5 h-5" />
        </button>

        {/* Notifications */}
        <button
          className="relative p-2 text-text-tertiary hover:text-amber hover:bg-panel-border/50 transition-colors"
          title="Notifications"
        >
          <Bell className="w-5 h-5" />
          <span
            className="absolute top-1 right-1 w-2 h-2 bg-amber rounded-full animate-pulse"
            style={{ boxShadow: '0 0 6px rgba(255, 149, 0, 0.8)' }}
          />
        </button>

        {/* Divider */}
        <div className="h-8 w-px bg-panel-border mx-2" />

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-3 hover:bg-panel-border/50 px-3 py-2 transition-colors"
          >
            <div className="w-8 h-8 bg-steel/20 border border-steel/50 flex items-center justify-center">
              <span className="text-steel font-mono text-sm font-bold">
                {getUserInitials()}
              </span>
            </div>
            <div className="text-left hidden lg:block">
              <div className="text-text-primary font-mono text-sm">{getUserDisplayName()}</div>
              <div className="text-text-tertiary font-mono text-[10px] uppercase tracking-wider">
                {user?.role || 'Operator'}
              </div>
            </div>
            <span className="text-text-tertiary text-xs">▼</span>
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-72 bg-panel-dark border border-panel-border shadow-panel z-50">
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
              <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

              {/* User Info Section */}
              <div className="p-4 border-b border-panel-border bg-background/50">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-amber/20 border border-amber/40 flex items-center justify-center">
                      <User className="w-5 h-5 text-amber" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary font-mono truncate">
                      {getUserDisplayName()}
                    </p>
                    <p className="text-xs text-text-tertiary font-mono truncate">{user?.email}</p>
                    {user?.role && (
                      <div className="mt-2 inline-flex items-center space-x-1.5">
                        <Shield className="w-3 h-3 text-steel" />
                        <span className="text-[10px] text-steel font-mono uppercase tracking-wider font-bold">
                          {user.role}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-2">
                <button
                  onClick={() => {
                    navigate('/settings');
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-text-secondary hover:text-amber hover:bg-amber/5 font-mono text-xs uppercase tracking-wider transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-panel-border py-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-error-red hover:bg-error-red/10 font-mono text-xs uppercase tracking-wider transition-colors group relative"
                >
                  <LogOut className="w-4 h-4" />
                  <span>System Logout</span>
                  <div className="absolute left-0 top-0 h-full w-1 bg-error-red opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>

              {/* Footer */}
              <div className="px-4 py-2 border-t border-panel-border bg-background/50">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-text-tertiary font-mono uppercase tracking-wider">
                    Session Active
                  </span>
                  <div
                    className="w-2 h-2 bg-success-green rounded-full animate-pulse"
                    style={{ boxShadow: '0 0 6px rgba(74,222,128,0.8)' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
